/**
 * PayslipsPanel (02.09, владелец) — «Сформировать расчётки» в OO «Зарплаты»:
 * из тех же цифр, что на экране (после всех правок ОО), рендерятся PNG
 * (payslip.ts) по одной на оператора; превью сеткой, тап — на весь экран;
 * «Разослать расчётки» — PNG уходят на сервер, тот шлёт каждому оператору
 * в staff-бот (sendPhoto) и кладёт копии в R2. Результат построчно
 * (отправлено / нет Telegram / ошибка). Кнопка рассылки активна только после
 * генерации именно этого набора цифр — поменял ставку → формируй заново.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { ConfirmModal } from "../../components/shared";
import { renderPayslip, type PsSal } from "./payslip";
import css from "./board.module.css";

interface Slip { staffId: string; name: string; total: number; blob: Blob; url: string }
interface SendRes { staff_id: string; name: string; status: "sent" | "no_telegram" | "fired" | "error"; error?: string }

/** «1 расчётка уйдёт», «2 расчётки уйдут», «5 расчёток уйдут». */
function slipsWord(n: number): string {
  const d = n % 10, h = n % 100;
  const w = d === 1 && h !== 11 ? "расчётка" : d >= 2 && d <= 4 && (h < 12 || h > 14) ? "расчётки" : "расчёток";
  return `${n} ${w} ${d === 1 && h !== 11 ? "уйдёт" : "уйдут"}`;
}
const fmtR = (n: number) => `₽${String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`;

export function PayslipsPanel({ rows, period, issuedBy }: { rows: PsSal[]; period: { from: string; to: string }; issuedBy: string }) {
  const [slips, setSlips] = useState<Slip[]>([]);
  const carryUsed = useRef<string | null>(null);      // 16.10: холд, с которым нарисованы расчётки (для сверки перед рассылкой)
  const [carryFail, setCarryFail] = useState(false);
  const [busy, setBusy] = useState<"gen" | "send" | null>(null);
  const [progress, setProgress] = useState(0);
  /* 06.09 (владелец): полноэкранная расчётка листается свайпом влево/вправо (и стрелками) — индекс, а не объект */
  const [viewIdx, setViewIdx] = useState<number | null>(null);
  const view = viewIdx != null ? slips[viewIdx] ?? null : null;
  const setView = (s: Slip | null) => setViewIdx(s ? slips.indexOf(s) : null);
  const step = (d: number) => setViewIdx((i) => (i == null || !slips.length ? i : (i + d + slips.length) % slips.length));
  const swipe = useRef<{ x: number; y: number; id: number } | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const flash = (t: string) => { setHint(t); window.setTimeout(() => setHint(null), 3200); };
  /* 06.09 (владелец): «Поделиться» — сперва системный шэринг (на телефоне откроет Telegram),
     иначе бот кладёт расчётку в личку нажавшему, откуда её пересылают в чат */
  const share = async (s: Slip) => {
    const file = new File([s.blob], `Расчётка ${s.name}.png`, { type: "image/png" });
    const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
    if (nav.canShare?.({ files: [file] })) {
      try { await nav.share({ files: [file], title: `Расчётка ${s.name}` }); return; } catch (e) { if ((e as Error).name === "AbortError") return; }
    }
    try {
      const fd = new FormData(); fd.append("file", s.blob, file.name); fd.append("name", s.name); fd.append("from", period.from); fd.append("to", period.to);
      await api("/api/finance/payslips/share", { method: "POST", body: fd, retries: 0 });
      flash("Отправил тебе в Telegram — перешли в нужный чат");
    } catch (e) { flash(e instanceof Error ? e.message : "Не удалось отправить"); }
  };
  useEffect(() => {
    if (viewIdx == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
      else if (e.key === "Escape") setViewIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIdx, slips.length]);
  const [result, setResult] = useState<SendRes[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sig, setSig] = useState<string>("");
  /* 03.10: подтверждение рассылки — ConfirmModal вместо window.confirm (A3) */
  const [ask, setAsk] = useState(false);
  const dd = (d: string) => `${d.slice(8)}.${d.slice(5, 7)}`;
  /* подпись набора: период + итоги — правка любой цифры обнуляет готовые расчётки */
  const curSig = `${period.from}_${period.to}_${rows.map((r) => `${r.staffId}:${r.total}`).join("|")}`;
  useEffect(() => () => { slips.forEach((s) => URL.revokeObjectURL(s.url)); }, [slips]);
  const stale = slips.length > 0 && sig !== curSig;

  const generate = async () => {
    setBusy("gen"); setErr(null); setResult(null); setProgress(0);
    slips.forEach((s) => URL.revokeObjectURL(s.url));
    const out: Slip[] = [];
    const issuedAt = new Date().toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    /* 16.10: холд с прошлых недель — на момент формирования и только для одной недели пт–чт (за месяц «долг» был бы
       ложным); не подтянулся — расчётки без блоков холда, но с предупреждением */
    let carry = new Map<string, number>();
    setCarryFail(false); carryUsed.current = null;
    if (isOneWeek(period)) {
      const c = await fetchCarry(period);
      if (c) { carry = c; carryUsed.current = carrySig(c, rows); } else setCarryFail(true);
    }
    try {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]!;
        const blob = await renderPayslip({ ...r, carry: carry.get(r.staffId) ?? 0 }, { from: period.from, to: period.to, issuedBy, issuedAt });
        out.push({ staffId: r.staffId, name: r.name, total: r.total, blob, url: URL.createObjectURL(blob) });
        setProgress(i + 1);
      }
      setSlips(out); setSig(curSig);
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : "Не удалось нарисовать расчётки"); }
    setBusy(null);
  };

  const send = async () => {
    setAsk(false);
    if (!slips.length || stale) return;
    setBusy("send"); setErr(null);
    /* холд мог измениться после формирования (выдали, отменили) — разослать старые цифры нельзя */
    if (carryUsed.current != null) {
      const now = await fetchCarry(period);
      if (!now || carrySig(now, rows) !== carryUsed.current) {
        setErr(now ? "Холд изменился после формирования (выдача или отмена) — сформируйте расчётки заново" : "Не удалось сверить холд — попробуйте ещё раз");
        setBusy(null);
        return;
      }
    }
    try {
      const fd = new FormData();
      fd.append("from", period.from); fd.append("to", period.to);
      for (const s of slips) fd.append(`file_${s.staffId}`, s.blob, `payslip_${s.staffId}.png`);
      const r = await api<{ ok: boolean; results: SendRes[] }>("/api/finance/payslips/send", { method: "POST", body: fd, retries: 0 });
      setResult(r.results ?? []);
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : "Рассылка не удалась"); }
    setBusy(null);
  };

  return (
    <section className={css.psPanel}>
      <div className={css.psHead}>
        <span className={css.psTitle}>Расчётки</span>
        <span className={css.psSub}>{rows.length} чел. · {period.from.slice(8)}.{period.from.slice(5, 7)} – {period.to.slice(8)}.{period.to.slice(5, 7)}</span>
      </div>
      <div className={css.psBtns}>
        <button type="button" className={`${css.psBtn} ${css.psGen}`} disabled={busy !== null || !rows.length} onClick={() => void generate()}>
          {busy === "gen" ? `Рисую… ${progress}/${rows.length}` : slips.length && !stale ? "Сформировать заново" : "Сформировать расчётки"}
        </button>
        <button type="button" className={`${css.psBtn} ${css.psSend}`} disabled={busy !== null || !slips.length || stale} onClick={() => setAsk(true)}
          title={stale ? "Цифры изменились — сформируйте заново" : ""}>
          {busy === "send" ? "Рассылаю…" : "Разослать расчётки"}
        </button>
      </div>
      {ask ? (
        <ConfirmModal tone="warn" title="Разослать расчётки?" actionLabel="Разослать" onAction={() => void send()} onCancel={() => setAsk(false)}>
          {slipsWord(slips.length)} операторам в Telegram за {dd(period.from)}–{dd(period.to)}.
        </ConfirmModal>
      ) : null}
      {stale ? <div className={css.psWarn}>Цифры изменились после формирования — сформируйте расчётки заново перед рассылкой.</div> : null}
      {carryFail && slips.length ? <div className={css.psWarn}>Холд не подтянулся — в этих расчётках его нет. Сформируйте заново, когда появится связь.</div> : null}
      {err ? <div className={css.psErr}>{err}</div> : null}
      {slips.length ? (
        <div className={css.psGrid}>
          {slips.map((s) => (
            <button key={s.staffId} type="button" className={css.psCard} onClick={() => setView(s)}>
              <img src={s.url} alt={s.name} className={css.psThumb} />
              <span className={css.psName}>{s.name}</span>
              <span className={css.psSum}>{fmtR(s.total)}</span>
              {result ? <span className={`${css.psSt} ${result.find((r) => r.staff_id === s.staffId)?.status === "sent" ? css.psOk : css.psBad}`}>
                {(() => { const st = result.find((r) => r.staff_id === s.staffId)?.status; return st === "sent" ? "отправлено" : st === "no_telegram" ? "нет Telegram" : st === "fired" ? "уволен" : st ? "ошибка" : ""; })()}
              </span> : null}
            </button>
          ))}
        </div>
      ) : null}
      {result ? <div className={css.psSub}>Отправлено {result.filter((r) => r.status === "sent").length} из {result.length}{result.some((r) => r.status !== "sent") ? ` · не ушло: ${result.filter((r) => r.status !== "sent").map((r) => r.name).join(", ")}` : ""}</div> : null}
      {view ? (
        <div className={css.psView} role="dialog" aria-label={`Расчётка ${view.name}`}
          onPointerDown={(e) => { swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId }; }}
          onPointerUp={(e) => {
            const s = swipe.current; swipe.current = null;
            if (!s || s.id !== e.pointerId) return;
            const dx = e.clientX - s.x, dy = e.clientY - s.y;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { step(dx < 0 ? 1 : -1); return; } /* свайп — следующая/предыдущая */
            /* 06.09 (владелец): тап ПО расчётке и по панели — ничего; тап МИМО — вернуться в «Зарплаты» */
            if (Math.hypot(dx, dy) < 12 && !(e.target as HTMLElement).closest(`img, .${css.psCap}`)) setViewIdx(null);
          }}
          onPointerCancel={() => { swipe.current = null; }}>
          <img key={view.staffId} src={view.url} alt={view.name} className={css.psViewImg} draggable={false} />
          <div className={css.psCap}>
            <button type="button" className={css.psArrow} aria-label="Предыдущая расчётка" onClick={() => step(-1)}>‹</button>
            <span className={css.psCapTxt}>{view.name} · {fmtR(view.total)} · {viewIdx! + 1}/{slips.length}</span>
            <button type="button" className={css.psArrow} aria-label="Следующая расчётка" onClick={() => step(1)}>›</button>
            <button type="button" className={css.psShare} onClick={() => void share(view)} title="Отправить расчётку в Telegram-чат">Поделиться</button>
          </div>
          {hint ? <div className={css.psHint}>{hint}</div> : null}
        </div>
      ) : null}
    </section>
  );
}

/* ── 16.10: холд для расчёток (GET /api/payouts/carry) ─────────────────────────────────────────────────────── */
/** Ровно одна зарплатная неделя: пятница … четверг. */
function isOneWeek(p: { from: string; to: string }): boolean {
  const f = Date.parse(`${p.from}T12:00:00Z`), t = Date.parse(`${p.to}T12:00:00Z`);
  return new Date(f).getUTCDay() === 5 && t - f === 6 * 86_400_000;
}
async function fetchCarry(p: { from: string; to: string }): Promise<Map<string, number> | null> {
  try {
    const c = await api<{ rows: { staff_id: string; carry: number }[] }>(`/api/payouts/carry?from=${p.from}&to=${p.to}`);
    return new Map((c.rows ?? []).map((x) => [x.staff_id, x.carry]));
  } catch { return null; }
}
const carrySig = (m: Map<string, number>, rows: PsSal[]) => rows.map((r) => `${r.staffId}:${m.get(r.staffId) ?? 0}`).join("|");
